import { createFileRoute } from "@tanstack/react-router";
import { Mail, Pencil, Palette } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import emailAsset from "@/assets/platform/networking/email-signature/email.webp.asset.json";
import editorAsset from "@/assets/platform/networking/email-signature/editor.webp.asset.json";
import variantsAsset from "@/assets/platform/networking/email-signature/variants.webp.asset.json";

const heroImg = emailAsset.url;
const heroAlt = "Email bất động sản mở trên iPhone hiển thị signature Banana của John Hoang — Luxury Property Strategist, số điện thoại, website và link View digital card dưới nội dung dự án Lumière Riverside";
const editorImg = editorAsset.url;
const editorAlt = "Trình chỉnh signature email Banana trên iPhone với preview live của John Hoang, nút Copy signature và Copy HTML cùng hướng dẫn cài đặt Gmail, Outlook, Apple Mail";
const variantsImg = variantsAsset.url;
const variantsAlt = "Nhiều mẫu signature email Banana cho Juliet Le — Marketing Manager, Head of Residential Sales và Business Development Director — cạnh iPhone preview thẻ Banana live";

export const Route = createFileRoute("/vi/platform/networking/email-signature")({
  head: () => ({
    meta: [
      { title: "Sinh signature email — banana.cards" },
      { name: "description", content: "Signature email branded cho Gmail, Outlook và Apple Mail — sinh từ thẻ Banana trong vài giây." },
      { property: "og:title", content: "Sinh signature email — banana.cards" },
      { property: "og:description", content: "Signature email branded cho mọi email client." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/networking/email-signature", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Signature email"
      title="Mỗi email,"
      italic="một moment thương hiệu."
      description="Sinh signature email branded từ thẻ Banana — drop thẳng vào Gmail, Outlook hoặc Apple Mail. Cập nhật thẻ cũng chảy vào signature."
      heroImage={heroImg}
      heroImageAlt={heroAlt}
      metrics={[{ v: "Gmail", l: "Outlook · Mail" }, { v: "1-click", l: "cài đặt" }, { v: "Auto", l: "cập nhật" }]}
      bullets={[
        { icon: Pencil, title: "Kéo từ thẻ", desc: "Tên, chức danh, ảnh, link và liên hệ — tất cả từ hồ sơ Banana." },
        { icon: Palette, title: "Thiết kế đẹp", desc: "Template curated render hoàn hảo trên mọi email client — không layout broken." },
        { icon: Mail, title: "Drop in & done", desc: "Copy-paste vào Gmail, Outlook hoặc Apple Mail — hướng dẫn đầy đủ." }
      ]}
      deepDives={[
        { eyebrow: "Thiết kế", title: "Đẹp", italic: "ở mọi nơi.", description: "Banana render signature với HTML email-client-safe — Gmail, Outlook desktop/web và Apple Mail đều hoàn hảo.", image: editorImg, imageAlt: editorAlt, reverse: false, points: ["Test cross-client", "Hỗ trợ ảnh Retina", "Màu & font branded", "Light & dark mode"] },
        { eyebrow: "Live", title: "Cập nhật thẻ,", italic: "cập nhật signature.", description: "Đổi chức danh, thêm link hay swap ảnh — signature phản ánh lần gửi kế tiếp.", image: variantsImg, imageAlt: variantsAlt, reverse: true, points: ["URL signature hosted", "Tự kéo hồ sơ mới nhất", "Signature mỗi thẻ", "Template team-wide"] }
      ]}
      howItWorks={[
        { title: "Chọn template", desc: "Từ thư viện signature Banana." },
        { title: "Sửa nội dung", desc: "Kéo từ thẻ — tweak nếu cần." },
        { title: "Copy signature", desc: "Hoặc lấy install link cho client." },
        { title: "Paste vào email app", desc: "Gmail, Outlook, Apple Mail — xong." }
      ]}
      capabilities={["Gmail, Outlook, Apple Mail", "HTML test cross-client", "URL signature hosted live", "Kéo từ thẻ Banana", "Template team-wide", "Nhiều signature mỗi user", "Màu & font tùy chỉnh", "Link tracked (tùy chọn)"]}
      faq={[
        { q: "Signature email Banana có gì?", a: "Tên, chức danh, công ty, ảnh, liên hệ, social link, nút 'Book a meeting', vCard download, banner image tùy chọn và link 'View my card'." },
        { q: "Email client nào hỗ trợ?", a: "Gmail (web, iOS, Android), Apple Mail, Outlook (web, desktop, mobile), Office 365, Yahoo Mail, ProtonMail, Spark, Superhuman, Front, HubSpot." },
        { q: "Cả team dùng chung template branded được không?", a: "Có — admin xây 1 template workspace với logo, màu, disclaimer rồi bulk-push cho mọi teammate." },
        { q: "Có track click trên signature link không?", a: "Có — wrap link trong layer tracking và click hiện trong analytics dashboard." }
      ]}
      outro={{ title: "Thẻ của bạn,", italic: "trong mọi email.", description: "Biến mỗi lần gửi thành touchpoint branded — tự động." }}
    />
  );
}